import React from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { SigningStep } from './useSigning'

interface SigningModalProps {
  isVisible: boolean
  toggleVisibility: () => void
  transitOperationId: number
  onSuccess: () => void
  step: SigningStep
  hasActiveSession: boolean
  onRetry: () => void
}

function SigningModal(props: Readonly<SigningModalProps>) {
  const {
    isVisible,
    toggleVisibility,
    transitOperationId,
    onSuccess,
    step,
    hasActiveSession,
    onRetry,
  } = props

  const { t } = useTranslation()

  const handleClose = () => {
    if (step === 'SUCCESS') {
      onSuccess()
    }
    toggleVisibility()
  }

  const isInProgress = step === 'IDLE' || step === 'PREPARING' || step === 'SIGNING' || step === 'SUBMITTING'

  const getStepMessage = (currentStep: SigningStep): string => {
    switch (currentStep) {
      case 'PREPARING':
        return t('signing.preparing')
      case 'SIGNING':
        return hasActiveSession ? t('signing.signingWithSession') : t('signing.signingHint')
      case 'SUBMITTING':
        return t('signing.submitting')
      case 'SUCCESS':
        return t('signing.success')
      case 'ERROR':
        return t('signing.error')
      default:
        return ''
    }
  }

  return (
    <Modal
      show={isVisible}
      dialogClassName="modal"
      size="lg"
      aria-labelledby="signing-modal-title"
      onHide={() => {
        if (!isInProgress) {
          handleClose()
        }
      }}
      animation={false}
      centered
      backdrop={isInProgress ? 'static' : true}
    >
      <Modal.Header>
        <Modal.Title className="d-flex align-self-center">
          <h2>{t('signing.title')}</h2>
        </Modal.Title>
        {!isInProgress && (
          <button
            type="button"
            className="btn btn-link no-underline btn-lg d-flex justify-content-center align-items-center px-0"
            onClick={handleClose}
          >
            <span>{t('buttons.close')}</span>
            <i className="fal fa-times fa-2x ms-2" />
          </button>
        )}
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center py-4">
          <div className="mb-3">
            <strong>
              {t('declaration.p5.transitOperation')}
              {transitOperationId}
            </strong>
          </div>

          {isInProgress && (
            <div className="d-flex flex-column align-items-center">
              <i className="fal fa-spinner fa-spin fa-2x mb-3" />
              <p className="mb-1">{getStepMessage(step)}</p>
            </div>
          )}

          {step === 'SUCCESS' && (
            <div className="d-flex flex-column align-items-center">
              <i className="fal fa-check-circle fa-2x mb-3 text-success" />
              <p className="text-success">{getStepMessage(step)}</p>
            </div>
          )}

          {step === 'ERROR' && (
            <div className="d-flex flex-column align-items-center">
              <i className="fal fa-exclamation-circle fa-2x mb-3 text-danger" />
              <p className="text-danger mb-1">{getStepMessage(step)}</p>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-end mt-3">
          {step === 'ERROR' && (
            <button
              type="button"
              className="btn btn-lg btn-primary mx-1 text-secondary shadow-sm"
              onClick={onRetry}
            >
              <i className="fal fa-redo me-2" />
              <span>{t('signing.retry')}</span>
            </button>
          )}
          {!isInProgress && (
            <button
              type="button"
              className="btn btn-lg btn-outline-dark mx-1"
              onClick={handleClose}
            >
              <span>{t('buttons.close')}</span>
            </button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default SigningModal
